import axios from "axios"; // tslint:disable-line match-default-export-name

import { gGateway } from "@utils/gateway";

import { nonConcurrent } from "@use/decorators";
import { EntityStore } from "@use/entity-store";
import { Collection } from "@use/models";

import { api } from "@courses-models/user/src/ts/config";
import { UnicUserModel } from "@courses-models/user/src/ts/models/UnicUserModel";
import {
  UserModel,
  UserTypes,
} from "@courses-models/user/src/ts/models/UserModel";

/**
 * Describes data returned by v1/unic/Classes/Students/{institutionNumber}/Class/{className} from Gyldendal Access
 * Service.
 */
type v2_Unic_Institution_GetClassStudents = {
  Team: string;
  InstitutionNumber: string;
  Class: {
    Name: string;
    InstitutionNumber: string;
    StudentCount: number;
  };
  GradeLevel: string;
  Gender: string;
  UserId: string;
  InstitutionId: string;
  SchoolComName: string;
  Name: string;
  FirstName: string;
  LastName: string;
  Email: string;
  Function: string;
  Birthdate: string;
}[];

/**
 * Memory cache of collections that have already been fetched by the client, so
 * that we don't need to fetch the same collections over and over again.
 */
const cache = new EntityStore<UserCollection>({ ttl: 600 });

/**
 * Iterable collection of users.
 */
export class UserCollection<
  T extends UserModel = UserModel
> extends Collection<T> {
  /**
   * Contains an internal set containing all users available within this
   * collection.
   */
  protected readonly collection: T[];

  /**
   * Cache collection of user's when this model is instantiated.
   */
  constructor(collection: T[]) {
    super();

    // Convert list of courses to an internal set
    this.collection = collection;
  }

  /**
   * Fetches a collection of courses that was shared with this student, and
   * converts each course into a well-typed model.
   */
  @nonConcurrent()
  public static async GET_BY_UNIC_CLASS(
    institutionId: string,
    className: string
  ): Promise<UserCollection<UnicUserModel>> {
    const cacheId = `UnicClass/${institutionId}/${className}`;

    // If this collection has already been cached, then return an instance
    // of this method directly from there
    if (cache.has(cacheId)) {
      return <UserCollection<UnicUserModel>>cache.get(cacheId);
    }

    // Fetch data about the user from Gyldendal Access Service
    const endpoint = `${api.gyldendalAccessService(
      "v2"
    )}/api/v2/Unic/Institution/GetClassStudents`;
    const requestBody = {
      InstitutionNumber: institutionId,
      ClassName: className,
    };
    const response = await axios.post<v2_Unic_Institution_GetClassStudents>(
      gGateway(endpoint),
      requestBody,
      {
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
      }
    );

    // Format data into an array of UnicUserModels
    const models = response.data.map((item) => {
      return UnicUserModel.CREATE({
        id: item.UserId,
        institutionId,
        mail: item.Email,
        name:
          item.FirstName || item.LastName
            ? `${item.FirstName} ${item.LastName}`
            : item.Name,
        userType: UserTypes.STUDENT,
      });
    });

    // Create our collection
    const collection = new UserCollection(models);

    // Cache the collection for future use
    cache.set(cacheId, collection);

    // Return the collection publicly
    return collection;
  }
}
